import { useState } from 'react'
import { Link } from 'react-router-dom'
import { roleOf, type Door, type Scenario } from '../../content/scenarios'
import styles from './DoorChooser.module.css'

/**
 * THE PACK PAGE — everything about Silfurberg, ending in the one choice that
 * starts it.
 *
 * It is the ONLY page between the shelf and the desk (founder, 2026-08-14). The
 * desk used to open on a threshold screen carrying the title, the role and the
 * blurb behind a TAKE THE DESK button — which was this page again, one click
 * later. That screen is gone.
 *
 * So the shape is: what this is → what you will be → what it is about → and
 * then, smaller, the variations. The doors are the last step of a page about a
 * mountain, not the subject of it; sized as the subject they read as a menu with
 * nothing around it.
 *
 * CHOOSING IS CONFIRMATORY, not immediate (founder, 2026-08-14): the rows are a
 * radio group with a default already selected, and one PLAY button commits. Two
 * reasons it is better than four links. A reader with no opinion never has to
 * form one — the realist door is picked for them and they press play. And the
 * click that spends money is a single, named, deliberate act rather than
 * whichever card they happened to touch.
 *
 * A door = a deep + a pressure + a want-bundle over a shared ground
 * (`2026-08-14-what-a-heimar-game-is.md` §11.5). The chooser is the
 * want-parameter become shelf architecture: the player self-selects intensity
 * before committing, which is the coarse grain of personalization and the one
 * the player performs themselves.
 *
 * TWO RULES IT MUST NOT BREAK:
 *  1. **Never reveal the deep.** Doors show pressure and tone. "Same mountain,
 *     different bottom" only works if the bottom stays unknown — a chooser that
 *     names door four's truth has spent the only thing that door has.
 *  2. **Never fake a door.** A door without a compiled pack is shown shut, not
 *     hidden and not selectable. Absence of `packId` is the single source of truth.
 *
 * A door can be shut for TWO UNRELATED REASONS, and they are kept visibly
 * apart on purpose:
 *   · NOT YET OPEN — nobody can go in; the pack does not exist yet.
 *   · NOT YET YOURS — the pack exists and other people are playing it; you have
 *     not been let in to play.
 * Dressing the second as the first would be a lie told to the one reader most
 * likely to ask about it.
 */
function pips(pressure: number) {
  return [1, 2, 3, 4].map((n) => (
    <span
      key={n}
      className={`${styles.pip} ${n <= pressure ? styles.pipOn : ''}`}
      aria-hidden="true"
    />
  ))
}

function DoorRow({
  door,
  selected,
  onSelect,
  locked,
}: {
  door: Door
  selected: boolean
  onSelect: (id: string) => void
  /** The reader may browse but has not been let in to play. */
  locked: boolean
}) {
  const built = Boolean(door.packId)
  const selectable = built && !locked

  const classes = [
    styles.door,
    selected ? styles.selected : '',
    selectable ? '' : styles.shut,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    /*
     * A real radio inside a label, visually hidden. Arrow-key navigation, the
     * roving tab stop and screen-reader grouping all come free from the platform
     * — reimplementing them on divs is how this control usually ends up
     * unreachable by keyboard.
     */
    <label className={classes}>
      <input
        className={styles.radio}
        type="radio"
        name="door"
        value={door.id}
        checked={selected}
        disabled={!selectable}
        onChange={() => onSelect(door.id)}
      />
      <span className={styles.marker} aria-hidden="true" />

      <span className={styles.body}>
        <span className={styles.name}>{door.name}</span>
        <span className={styles.doorLine}>{door.line}</span>
      </span>

      <span className={styles.meta}>
        <span
          className={styles.dial}
          role="img"
          aria-label={`pressure ${door.pressure} of 4`}
        >
          {pips(door.pressure)}
        </span>
        {/* The unbuilt fact outranks the not-yours one: a door that does not
            exist is not waiting on the reader's standing. */}
        {!built ? (
          <span className={styles.state}>NOT YET OPEN</span>
        ) : locked ? (
          <span className={styles.state}>NOT YET YOURS</span>
        ) : null}
      </span>
    </label>
  )
}

export default function DoorChooser({
  scenario,
  locked = false,
}: {
  scenario: Scenario
  /**
   * True for a signed-in reader who has not been approved to play. They reach
   * this page normally — browsing is open — and it is where the limit is stated,
   * BEFORE a click that could only end in a refusal.
   */
  locked?: boolean
}) {
  const doors = scenario.doors ?? []

  /*
   * THE DEFAULT IS THE FIRST PLAYABLE DOOR, which is the realist one: the
   * manifest orders doors as the credibility ladder, and the realist door
   * anchors the shelf (§11.3). So the reader who does not want to choose gets
   * the door the pack is most itself behind, and presses play.
   *
   * "First playable" rather than a hardcoded id so it stays right as doors two
   * to four compile — and so it can never default to a door with no pack.
   */
  const [selected, setSelected] = useState(
    () => doors.find((d) => d.packId)?.id ?? '',
  )

  const chosen = doors.find((d) => d.id === selected && d.packId)
  const canPlay = !locked && Boolean(chosen)

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <h1 className={styles.title}>{scenario.title}</h1>

        {/* The one piece of ratified shelf copy for this page (four-doors §6). */}
        <p className={styles.line}>
          the mountain is a fact; what&rsquo;s inside it is your choice
        </p>

        {/* What you will BE — the role, before what it is about. */}
        <p className={styles.role}>{roleOf(scenario)}</p>

        {/*
          The description. It lived on the desk's threshold screen until that
          screen was removed; this is where a reader deciding whether to go in
          actually needs it.
        */}
        <p className={styles.blurb}>{scenario.blurb}</p>

        <p className={styles.tags}>{scenario.tags.join(' · ')}</p>

        {/*
          The hinge of the page: everything above is the mountain, everything
          below is the way in. Stated plainly because the doors are unlabelled
          until the founder names them, and a reader needs to know these are the
          same story at different pressures rather than four different products.
          ⚠ COPY IS PLACEHOLDER — the founder writes the real thing.
        */}
        {/* aria-hidden: the radio group carries this same text as its <legend>,
            and without this a screen reader announces the heading twice. */}
        <div className={styles.pick} aria-hidden="true">
          <span className={styles.pickLabel}>choose a way down</span>
        </div>

        <fieldset className={styles.doors}>
          <legend className={styles.legend}>Choose a way down</legend>
          {doors.map((d) => (
            <DoorRow
              key={d.id}
              door={d}
              selected={d.id === selected}
              onSelect={setSelected}
              locked={locked}
            />
          ))}
        </fieldset>

        {canPlay && (
          <Link
            className={styles.play}
            to={`/p/${scenario.id}?door=${chosen!.id}`}
          >
            PLAY
          </Link>
        )}

        {/*
          Stated once, under the doors — not as a banner over them. The reader
          came to look at a mountain; the administrative fact about their account
          is real but it is not the subject of the page.
          ⚠ COPY IS PLACEHOLDER — the founder writes the real thing.
        */}
        {locked && (
          <p className={styles.standing}>
            you can look around. going down opens when you are let in.
          </p>
        )}

        <Link to="/" className={styles.back}>
          ‹ BACK
        </Link>
      </div>
    </div>
  )
}
